import { LinkRounded } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material"
import { memo } from "react"
import { generatePath, Link as RouterLink, useNavigate } from "react-router"
import type { SettingsConnectorType } from "@oboku/shared"
import { useConnector } from "./useConnector"
import { useConnectors } from "./useConnectors"
import { links } from "@oboku/shared"
import { PageSection } from "../common/PageSection"
import { ROUTES } from "../navigation/routes"

const ConnectorListItem = memo(function ConnectorListItem({
  id,
  onClick,
  connectorType,
}: {
  id: string
  connectorType: SettingsConnectorType
  onClick: () => void
}) {
  const { data: connector } = useConnector({ id, type: connectorType })

  return (
    <ListItemButton onClick={onClick}>
      <ListItemIcon>
        <LinkRounded />
      </ListItemIcon>
      <ListItemText
        primary={connector?.url ?? connector?.username}
        secondary={
          connector?.url ? `${connector?.username}@********` : "********"
        }
      />
    </ListItemButton>
  )
})

export const ConnectorInfoSection = memo(function ConnectorInfoSection({
  connectorType,
  maxConnectors,
}: {
  connectorType: SettingsConnectorType
  maxConnectors?: number
}) {
  const { data: connectors } = useConnectors({ type: connectorType })
  const navigate = useNavigate()
  const connectorCount = connectors?.length ?? 0
  const canAdd = maxConnectors === undefined || connectorCount < maxConnectors
  const hasConnectors = connectorCount > 0

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      <Stack
        sx={{
          px: 2,
          pt: 2,
          gap: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: "text.secondary",
          }}
        >
          Connectors
        </Typography>
        <Alert severity="info" variant="standard">
          Learn more about connectors{" "}
          <MuiLink
            href={links.documentationConnectors}
            rel="noopener noreferrer"
            target="_blank"
          >
            here
          </MuiLink>
        </Alert>
      </Stack>
      {canAdd && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: { sm: "flex-start" },
            mt: 2,
            px: 2,
          }}
        >
          <Button
            component={RouterLink}
            to={generatePath(ROUTES.INTEGRATION_CONNECTORS_NEW, {
              type: connectorType,
            })}
            variant="contained"
          >
            Add a connector
          </Button>
        </Box>
      )}
      {hasConnectors && (
        <PageSection>
          {connectors?.map((connector) => (
            <ConnectorListItem
              id={connector.id}
              key={connector.id}
              connectorType={connectorType}
              onClick={() => {
                navigate(
                  generatePath(ROUTES.INTEGRATION_CONNECTORS_EDIT, {
                    type: connectorType,
                    id: connector.id,
                  }),
                )
              }}
            />
          ))}
        </PageSection>
      )}
    </Box>
  )
})
